import React from 'react';
import { ArrowLeftRight, Download, Upload, TrendingUp } from 'lucide-react';
interface AccountDetailsPanelProps {
  accountType: 'main' | 'futures' | 'inverse-futures' | 'earn';
}
export function AccountDetailsPanel({
  accountType
}: AccountDetailsPanelProps) {
  // Mock data based on account type
  const getAccountDetails = () => {
    switch (accountType) {
      case 'main':
        return {
          balance: 4.79,
          health: 98.78,
          marginHealth: 98.78,
          leverage: 10.14,
          collateralValue: 4.79,
          upnl: 0.08756,
          marginEquity: 4.88,
          usedMargin: 4.94,
          availMargin: -0.05973,
          maintenanceMargin: 3.95
        };
      case 'futures':
        return {
          balance: 0.0,
          health: null,
          marginHealth: null,
          leverage: null,
          collateralValue: 0.0,
          upnl: 0.0,
          marginEquity: 0.0,
          usedMargin: 0.0,
          availMargin: 0.0,
          maintenanceMargin: 0.0
        };
      case 'inverse-futures':
        return {
          balance: 0.0,
          health: null,
          marginHealth: null,
          leverage: null,
          collateralValue: 0.0,
          upnl: 0.0,
          marginEquity: 0.0,
          usedMargin: 0.0,
          availMargin: 0.0,
          maintenanceMargin: 0.0
        };
      case 'earn':
        return {
          balance: 3247.89,
          totalRewards: 42.15,
          weeklyRewards: 42.15
        };
      default:
        return null;
    }
  };
  const details = getAccountDetails();
  if (!details) return null;
  const getHealthColor = (health: number | null) => {
    if (health === null) return 'text-text-secondary';
    if (health > 80) return 'text-success';
    if (health > 50) return 'text-warning';
    return 'text-danger';
  };
  return <div className="bg-background-secondary border border-border-subtle rounded-[20px] p-6 space-y-6" data-id="element-985">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-border-subtle" data-id="element-986">
        <h3 className="text-lg font-semibold text-text-primary" data-id="element-987">
          {accountType === 'main' && 'Main wallet details'}
          {accountType === 'futures' && 'Futures wallet details'}
          {accountType === 'inverse-futures' && 'Inverse Futures wallet details'}
          {accountType === 'earn' && 'Earn wallet details'}
        </h3>
      </div>

      {/* Account Details */}
      <div className="space-y-4" data-id="element-988">
        {accountType !== 'earn' ? <>
            <div className="flex items-center justify-between" data-id="element-989">
              <span className="text-sm text-text-secondary" data-id="element-990">Balance</span>
              <span className="text-sm font-numbers text-text-primary" data-id="element-991">
                {details.balance.toFixed(2)}{' '}
                <span className="text-text-secondary" data-id="element-992">CHF</span>
              </span>
            </div>

            {details.health !== null && <div className="flex items-center justify-between" data-id="element-993">
                <span className="text-sm text-text-secondary" data-id="element-994">Health</span>
                <span className={`text-sm font-numbers font-medium ${getHealthColor(details.health)}`} data-id="element-995">
                  {details.health > 0 ? details.health.toFixed(2) + '%' : '-'}
                </span>
              </div>}

            {details.marginHealth !== null && <div className="flex items-center justify-between" data-id="element-996">
                <span className="text-sm text-text-secondary" data-id="element-997">
                  Margin health
                </span>
                <span className={`text-sm font-numbers font-medium ${getHealthColor(details.marginHealth)}`} data-id="element-998">
                  {details.marginHealth > 0 ? details.marginHealth.toFixed(2) + '%' : '-'}
                </span>
              </div>}

            {details.leverage !== null && <div className="flex items-center justify-between" data-id="element-999">
                <span className="text-sm text-text-secondary" data-id="element-1000">Leverage</span>
                <span className="text-sm font-numbers text-text-primary" data-id="element-1001">
                  {details.leverage > 0 ? details.leverage.toFixed(2) + 'x' : '-'}
                </span>
              </div>}

            <div className="h-px bg-border-subtle my-4" data-id="element-1002" />

            <div className="flex items-center justify-between" data-id="element-1003">
              <span className="text-sm text-text-secondary" data-id="element-1004">
                Collateral value
              </span>
              <span className="text-sm font-numbers text-text-primary" data-id="element-1005">
                {details.collateralValue.toFixed(2)}{' '}
                <span className="text-text-secondary" data-id="element-1006">CHF</span>
              </span>
            </div>

            <div className="flex items-center justify-between" data-id="element-1007">
              <span className="text-sm text-text-secondary" data-id="element-1008">UP&L</span>
              <span className={`text-sm font-numbers ${details.upnl >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-1009">
                {details.upnl >= 0 ? '+' : ''}
                {details.upnl.toFixed(5)}{' '}
                <span className="text-text-secondary" data-id="element-1010">CHF</span>
              </span>
            </div>

            <div className="h-px bg-border-subtle my-4" data-id="element-1011" />

            <div className="flex items-center justify-between" data-id="element-1012">
              <span className="text-sm text-text-secondary" data-id="element-1013">Margin equity</span>
              <span className="text-sm font-numbers text-text-primary" data-id="element-1014">
                {details.marginEquity.toFixed(2)}{' '}
                <span className="text-text-secondary" data-id="element-1015">CHF</span>
              </span>
            </div>

            <div className="flex items-center justify-between" data-id="element-1016">
              <span className="text-sm text-text-secondary" data-id="element-1017">Used margin</span>
              <span className="text-sm font-numbers text-text-primary" data-id="element-1018">
                {details.usedMargin.toFixed(2)}{' '}
                <span className="text-text-secondary" data-id="element-1019">CHF</span>
              </span>
            </div>

            <div className="flex items-center justify-between" data-id="element-1020">
              <span className="text-sm text-text-secondary" data-id="element-1021">Avail. margin</span>
              <span className={`text-sm font-numbers ${details.availMargin >= 0 ? 'text-text-primary' : 'text-danger'}`} data-id="element-1022">
                {details.availMargin.toFixed(5)}{' '}
                <span className="text-text-secondary" data-id="element-1023">CHF</span>
              </span>
            </div>

            <div className="flex items-center justify-between" data-id="element-1024">
              <span className="text-sm text-text-secondary" data-id="element-1025">
                Maintenance margin
              </span>
              <span className="text-sm font-numbers text-text-primary" data-id="element-1026">
                {details.maintenanceMargin.toFixed(2)}{' '}
                <span className="text-text-secondary" data-id="element-1027">CHF</span>
              </span>
            </div>
          </> : <>
            <div className="flex items-center justify-between" data-id="element-1028">
              <span className="text-sm text-text-secondary" data-id="element-1029">Balance</span>
              <span className="text-sm font-numbers text-text-primary" data-id="element-1030">
                {details.balance.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}{' '}
                <span className="text-text-secondary" data-id="element-1031">USD</span>
              </span>
            </div>

            <div className="flex items-center justify-between" data-id="element-1032">
              <span className="text-sm text-text-secondary" data-id="element-1033">Total rewards</span>
              <span className="text-sm font-numbers text-success" data-id="element-1034">
                +{details.totalRewards.toFixed(2)}{' '}
                <span className="text-text-secondary" data-id="element-1035">USD</span>
              </span>
            </div>

            <div className="h-px bg-border-subtle my-4" data-id="element-1036" />

            <div className="bg-background-elevated rounded-lg p-4" data-id="element-1037">
              <div className="flex items-center gap-2 mb-2" data-id="element-1038">
                <TrendingUp size={16} className="text-success" data-id="element-1039" />
                <span className="text-xs text-text-secondary" data-id="element-1040">
                  Weekly Rewards
                </span>
              </div>
              <div className="text-lg font-numbers text-success" data-id="element-1041">
                +{details.weeklyRewards.toFixed(2)}{' '}
                <span className="text-sm text-text-secondary" data-id="element-1042">USD</span>
              </div>
            </div>
          </>}
      </div>

      {/* Action Buttons */}
      <div className="space-y-2 pt-4 border-t border-border-subtle" data-id="element-1043">
        <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary border border-border-subtle hover:border-border-default rounded-lg transition-colors" data-id="element-1044">
          <ArrowLeftRight size={16} data-id="element-1045" />
          Transfer
        </button>
        <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary border border-border-subtle hover:border-border-default rounded-lg transition-colors" data-id="element-1046">
          <Upload size={16} data-id="element-1047" />
          Withdraw
        </button>
        <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm bg-brand-purple hover:bg-brand-purple-light text-white rounded-lg transition-colors" data-id="element-1048">
          <Download size={16} data-id="element-1049" />
          Deposit
        </button>
      </div>
    </div>;
}